/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CallbackRulesPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Main page for Callback Rules management with list view - simplified using hooks.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { navigateToCallbackRuleDuplicate } from './callbackRulesActions';
import { useCallbackRulesList } from './hooks/useCallbackRulesList';
import { deleteCallbackRule } from './api/callbackRulesApi';
import type { CallbackRule } from './types';

export default function CallbackRulesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; rule: CallbackRule | null }>({
    isOpen: false,
    rule: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useCallbackRulesList({
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  const handleAdd = useCallback(() => {
    navigate('/pbx/applications/callback/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (rule: CallbackRule) => {
      navigate(`/pbx/applications/callback/${rule.uuid || rule.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (rule: CallbackRule) => {
      navigateToCallbackRuleDuplicate(navigate, String(rule.uuid || rule.id));
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((rule: CallbackRule) => {
    setDeleteModal({ isOpen: true, rule });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.rule) return;

    setIsDeleting(true);
    try {
      await deleteCallbackRule(String(deleteModal.rule.uuid || deleteModal.rule.id));

      showSuccess(t('callbackRules:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, rule: null });
      await refresh();
    } catch (error) {
      showError(error?.message || t('callbackRules:messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.rule, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback(
    (item: CallbackRule) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('callbackRules:actions.edit')} position="top">
          <button
            onClick={() => handleEdit(item)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('callbackRules:actions.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(item)}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('callbackRules:actions.delete')} position="top">
          <button
            onClick={() => handleDeleteClick(item)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleAdd}
          title={t('callbackRules:title')}
          titleIcon={<PhoneIncoming className="h-5 w-5 text-primary" />}
          addButtonText={t('callbackRules:actions.add')}
          renderActions={renderRowActions}
          searchPlaceholder={t('callbackRules:searchPlaceholder')}
          emptyMessage={t('callbackRules:emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, rule: null })}
        onConfirm={handleDeleteConfirm}
        title={t('callbackRules:messages.deleteTitle')}
        message={t('callbackRules:messages.confirmDelete', { name: deleteModal.rule?.name || '' })}
        confirmText={t('callbackRules:actions.delete')}
        cancelText={t('callbackRules:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
